import { App, AppState } from '@capacitor/app';
import { Injectable } from '@angular/core';
import { CrudApiProvider } from '../crud-api/crud-api';
import { UserProvider } from '../user/user';
import { EventEmitter } from '../../includes/eventEmitter';
import * as debug from 'debug';
import * as _ from 'lodash';
import { ConfigProvider } from '../config/config';
import { ToolsProvider } from '../tools/tools';
import { default as socketio } from 'socket.io-client';
import { AlertController, ToastController } from 'ionic-angular';
import { EnvVariables } from '../../environments/environment';
import * as moment from 'moment';
import { DomSanitizer } from '@angular/platform-browser';
@Injectable()
export class LiveChatProvider extends EventEmitter {
  log = debug('chat:LiveChatProvider');

  socketConnection;
  localConversations = [];
  popularConversations = [];

  discoverConversations = [];
  activeConversations = [];

  onlineUsersByRoom = {};
  onlineUserIds = [];
  onlineUsers = [];

  focused = document.hasFocus();

  constructor(
    public crudApiProvider: CrudApiProvider,
    public user: UserProvider,
    public config: ConfigProvider,
    public tools: ToolsProvider,
    public alertCtrl: AlertController,
    public toastCtrl: ToastController,
    public sanitizer: DomSanitizer,
    public env: EnvVariables
  ) {
    super();
    this.log('constructed');
    this.joinRoomSocket('main');
  }

  setupListeners() {
    App.addListener('appStateChange', (state: AppState) => {
      this.log('appStateChange', state);
      if (_.get(state, 'isActive') === false) {
        this.socketConnection.emit('app-unfocused');
      } else {
        this.socketConnection.emit('app-focused');
        this.tools.emit('refreshRoomPrompts');
      }
    });

    this.log('setupListeners');
    this.socketConnection.on('connect', data => {
      this.emit('connected');
    });
    this.socketConnection.on('disconnect', data => {
      this.emit('disconnected');
    });
    this.socketConnection.on('users-online', data => {
      const { socketId } = data;
      this.log('ROOM: %s | users-online', socketId, data);
      const ids = _.get(data, 'onlineUserIds');
      const users = _.get(data, 'onlineUsers');
      this.onlineUserIds = _.size(ids) ? ids : this.onlineUserIds;
      this.onlineUsers = _.size(users) ? users : this.onlineUsers;
      // this.onlineUsersByRoom = _.get(data, 'onlineUsersByRoom');
      // this.refreshLocalConversationsOnlineUsers();
      this.emit('socket--users-online--' + socketId, data);
    });
    this.socketConnection.on('message-create', (data, meta) => {
      const { socketId } = data;
      const localConv = this.getLocalRoomBySocketId(socketId);

      this.log(
        'ROOM: %s | message-create',
        socketId,
        data,
        localConv,
        this.localConversations
      );
      this.addMessageToRoom(localConv?._id, data);
      this.emit('socket--message-create--' + socketId, data);

      this.addSocketIdToLocalStorage(socketId, data);
    });

    this.socketConnection.on('message-update', (data, meta) => {
      const socketId = data?.socketId || meta?.socketId;
      const localConv = this.getLocalRoomBySocketId(socketId);
      this.log('ROOM: %s | message-update', socketId, data, meta, localConv);

      this.addMessageToRoom(localConv?._id, data);
      this.emit('socket--message-update--' + socketId, data);
    });

    this.socketConnection.on('message-delete', data => {
      const { socketId } = data;
      this.log('ROOM: %s | message-delete', socketId, data);
    });

    this.socketConnection.on('reply-create', data => {
      const { socketId, messageId } = data;
      const localConv = this.getLocalRoomBySocketId(socketId);
      this.log('ROOM: %s | reply-create', socketId, localConv, data);
      this.addReplyToMessage(localConv._id, data);
      this.emit('socket--reply-create--' + socketId, data);
      this.emit('socket--reply-create--' + messageId, data);
    });

    this.socketConnection.on('reply-update', data => {
      const { socketId } = data;
      this.log('ROOM: %s | reply-update', socketId, data);
    });

    this.socketConnection.on('reply-delete', data => {
      const { socketId } = data;
      this.log('ROOM: %s | reply-delete', socketId, data);
    });

    this.socketConnection.on('reactions-update', data => {
      const { socketId, messageOrReplyId } = data;
      // const localConv = this.getLocalRoomBySocketId(socketId);
      this.log('ROOM: %s | reaction', socketId, data?.messageOrReply, data);

      if (data?.messageOrReplyId) {
        this.emit('socket--reaction--' + data?.messageOrReplyId, data);
      }
    });

    this.socketConnection.on('user-join', data => {
      const { socketId } = data;
      this.log('ROOM: %s | user-join', socketId, data);
      this.emit('socket--user-join--' + socketId, data);
      this.toastCtrl
        .create({
          message: `${_.get(data, 'username')} joined the room.`,
          duration: 3500,
          position: 'bottom',
        })
        .present();
    });

    this.socketConnection.on('user-leave', data => {
      const { socketId } = data;
      this.log('ROOM: %s | user-leave', socketId, data);
      this.emit('socket--user-leave--' + socketId, data);

      this.toastCtrl
        .create({
          message: `${_.get(data, 'username')} left the room.`,
          duration: 3500,
          position: 'bottom',
        })
        .present();
    });

    this.socketConnection.on('typing-start', data => {
      const { socketId, messageId } = data;
      const sId = messageId || socketId;
      this.log('ROOM: %s | typing start', sId, data);
      this.emit('socket--typing-start--' + sId, data);
    });

    this.socketConnection.on('typing-stop', data => {
      const { socketId, messageId } = data;
      const sId = messageId || socketId;
      this.log('ROOM: %s | typing stop', sId, data);
      this.emit('socket--typing-stop--' + sId, data);
    });
  }

  addSocketIdToLocalStorage(socketId: string, data): void {
    this.emit('new-live-chat-message', { data });

    const storageKey = 'recently-updated-chats';

    const storedData = localStorage.getItem(storageKey);
    let chatArray: string[] = storedData
      ? (JSON.parse(storedData) as string[])
      : [];

    if (chatArray.indexOf(socketId) === -1) {
      chatArray.push(socketId);
      localStorage.setItem(storageKey, JSON.stringify(chatArray));
    }
  }

  getLocalRoomBySocketId(socketId: string) {
    return _.get(this.getConversationMap('socketId'), socketId);
  }

  addMessageToRoom(conversationId: string, message: any) {
    const localRoom = this.getLocalRoom(conversationId);

    try {
      let foundMessage = _.findIndex(localRoom?.messages, m => {
        if (
          (!m._id && !m?.dates?.updated) ||
          (m?._id === message?._id && m?._id && message?._id)
        ) {
          return m;
        }
      });

      if (foundMessage >= 0) {
        setTimeout(() => {
          localRoom.messages[foundMessage] = _.merge(
            localRoom?.messages[foundMessage],
            message
          );
          localRoom.messages[foundMessage].dates = message.dates;
        }, 250);
      } else {
        localRoom.messages.unshift(message);
      }

      this.emit('add-message-to-room');
    } catch (e) {}
  }

  async deleteMessageOrReply(message: any) {
    const deleted = await this.crudApiProvider.delete(
      `/api/v2/lc/${message?.messageId ? 'replies' : 'messages'}/${_.get(
        message,
        '_id'
      )}`
    );
  }

  async findReply(replyId: string) {
    return this.crudApiProvider.get(`/api/v2/lc/replies/${replyId}`);
  }

  addReplyToMessage(conversationId: string, reply: any) {
    const localRoom = this.getLocalRoom(conversationId);
    const localMessage = _.find(
      localRoom.messages,
      m => m._id === _.get(reply, 'messageId')
    );
    this.log('addReplyToMessage', conversationId, localRoom.replies);
    if (!_.size(localMessage?.replies)) {
      _.set(localMessage, 'replies', [reply]);
    } else {
      let foundReply = _.findIndex(
        localMessage.replies,
        r => !r._id && !r?.dates?.updated
      );

      if (foundReply >= 0) {
        setTimeout(() => {
          localMessage.replies[foundReply] = reply;
        }, 250);
      } else {
        localMessage?.replies.push(reply);
      }
    }
  }

  async markAsOffTopic(messageId: string) {
    await this.crudApiProvider.put(
      `/api/v2/lc/messages/${messageId}/off-topic`
    );
  }

  async voteOnPoll(messageId: string, pollData) {
    this.log('live-chat-poll-vote', pollData);
    await this.crudApiProvider
      .put(`/api/v2/lc/messages/${messageId}/poll-vote`, { pollData })
      .catch(e => {
        const err =
          _.get(e, 'error.message') ||
          _.get(e, 'error.error') ||
          _.get(e, 'message') ||
          'Something went wrong';
        return Promise.reject(err);
      });
  }

  // addReactionToMessage(conversationId: string, messageId) {}

  // addReactionToReply(conversationId: string) {}

  async getMessage(messageId) {
    return this.crudApiProvider.get(`/api/v2/lc/messages/${messageId}`);
  }

  getLocalRoom(conversationId: string) {
    return _.get(this.getConversationMap(), conversationId);
  }

  async joinRoomSocket(
    socketId: string // a replies socketId is the message._id
  ) {
    socketId = _.toLower(socketId);
    this.log('joinRoom | %s', socketId);
    if (!socketId) {
      // return alert('joinRoom | no id provided');
    }

    if (!this.socketConnection || socketId === 'main') {
      this.socketConnection = await socketio.io(
        this.env.apiURLv2 + '/live-chat',
        {
          auth: {
            token: CrudApiProvider.authKey,
          },
          query: { socketId },
          transports: ['websocket'],
        }
      );
      this.setupListeners();
    } else {
      this.socketConnection.emit('join', socketId);
    }
  }

  async join(conversationId: string) {
    await this.crudApiProvider.post(`/api/v2/lc/rooms/${conversationId}/join`);
  }

  async leave(conversationId: string) {
    await this.crudApiProvider.post(`/api/v2/lc/rooms/${conversationId}/leave`);
  }

  async mute(conversationId: string) {
    const { data } = await this.crudApiProvider.post(
      `/api/v2/lc/rooms/${conversationId}/mute`
    );
    this.log('mute', _.cloneDeep(data?.dndUserIds));
    await this.updateLocalConversation(conversationId, data, ['dndUserIds']);
  }

  async unmute(conversationId: string) {
    const { data } = await this.crudApiProvider.post(
      `/api/v2/lc/rooms/${conversationId}/unmute`
    );
    this.log('unmute', _.cloneDeep(data?.dndUserIds));
    await this.updateLocalConversation(conversationId, data, ['dndUserIds']);
  }

  async updateLocalConversation(
    conversationId: string,
    roomObj,
    fields?: string[]
  ) {
    this.log(
      'updateLocalConversation | update',
      conversationId,
      _.cloneDeep(roomObj)
    );
    let lr = this.getLocalRoom(conversationId);
    if (!lr) {
      this.localConversations.push(roomObj);
      this.log(
        'updateLocalConversation | new push',
        _.cloneDeep(this.localConversations)
      );
    } else {
      this.log('updateLocalConversation | merging local');
      if (fields) {
        _.each(fields, field => {
          this.log('field', field, roomObj[field]);
          _.set(lr, field.k, roomObj[field]);
        });
        this.localConversations = _.map(this.localConversations, r => {
          if (r?._id === lr?._id) r = { ...r, ...lr };
        });
      } else {
        this.localConversations = _.map(this.localConversations, r => {
          if (r?._id === lr?._id) r = { ...r, ...lr };
        });
      }
    }
    this.emit('local-room-update-' + conversationId, lr);
    return lr;
  }

  handleMessage() {}

  getConversationMap(key = '_id') {
    return _.mapKeys(this.localConversations, key);
  }

  getConversations(): Promise<any> {
    return this.crudApiProvider
      .get('/api/v2/lc/rooms', {
        sortBy: 'dates.updated:desc',
      })
      .then(async res => {
        const data = await this.mapOpenChats(res.data);
        this.mergeLocalConversations(data);
        this.log('localConversations', this.localConversations);
        return Promise.resolve(this.localConversations || []);
      })
      .catch(e => {
        this.log('error getting conversations', e);
        const toast = this.toastCtrl.create({
          message:
            'An error occurred while retrieving your conversations. Please check your internet connection and try again. If the issue continues, please contact us for help.',
          position: 'top',
          duration: 5000,
        });
        toast.present();
        return Promise.reject('error getting conversations');
      });
  }

  async mergeLocalConversations(data) {
    this.localConversations = _.merge(this.localConversations, data);
    this.refreshLocalConversationsOnlineUsers();
  }

  async refreshLocalConversationsOnlineUsers() {
    _.each(this.localConversations, c => {
      const userIds = new Set(c?.userIds);
      const onlineUsers = _.filter(this.onlineUsers, u => userIds.has(u._id));
      _.set(c, '_onlineUsers', onlineUsers);
      this.log(
        'refreshLocalConversationOnlineUsers',
        c?.socketId,
        c?._onlineUsers
      );
    });
  }

  emitTyping(socketId: string, messageId?: string) {
    this.socketConnection.emit('typing-start', {
      socketId,
      data: {
        ...(messageId && { messageId }),
        user: this.user.getPublicInfo(),
      },
    });
  }

  emitTypingStopped(socketId: string, messageId?: string) {
    this.socketConnection.emit('typing-stop', {
      socketId,
      data: {
        ...(messageId && { messageId }),
        user: this.user.getPublicInfo(),
      },
    });
  }

  async mapOpenChats(rooms) {
    const currentHour = new Date().getHours();
    _.each(rooms, rm => {
      if (_.get(rm, 'svg')) {
        rm.svgSanitized = this.sanitizer.bypassSecurityTrustHtml(rm.svg);
      }
      if (!_.get(rm, 'openTime')) return;
      const { from, to } = rm?.openTime;
      if (from && to) {
        if (from <= to) {
          rm.isClosed = !(currentHour >= from && currentHour <= to);
          this.log(
            'mapOpenChats 1 | %s time: %s is %s between %s - %s',
            rm.name,
            currentHour,
            rm.isClosed ? 'NOT' : 'IS',
            from,
            to
          );
        } else if (from > to) {
          rm.isClosed = !(currentHour >= from || currentHour <= to);
          this.log(
            'mapOpenChats 2 | %s time: %s is %s between %s - %s',
            rm.name,
            currentHour,
            rm.isClosed ? 'NOT' : 'IS',
            from,
            to
          );
        }
      }
    });
    return rooms;
  }

  reloadPublicRooms = _.debounce(() => {
    this.log('viewDidEnter | reload');
    return this.localConversations || [];
  });

  async getPublicRooms(): Promise<any> {
    this.localConversations = [];
    return this.crudApiProvider
      .get('/api/v2/lc/rooms/public', {
        sortBy: 'dates.updated:desc',
      })
      .then(async res => {
        const data = await this.mapOpenChats(res.data);
        await this.mergeLocalConversations(data);
        this.log('localConversations', this.localConversations);
        return Promise.resolve(this.localConversations || []);
      })
      .catch(e => {
        this.log('error getting conversations', e);
        const toast = this.toastCtrl.create({
          message:
            'An error occurred while retrieving your conversations. Please check your internet connection and try again. If the issue continues, please contact us for help.',
          position: 'top',
          duration: 5000,
        });
        toast.present();
        return Promise.reject('error getting conversations');
      });
  }

  async getPopularRooms(): Promise<any> {
    return this.crudApiProvider
      .get('/api/v2/lc/rooms/popular', {
        sortBy: 'dates.updated:asc',
      })
      .then(async res => {
        this.popularConversations = res.data;
        this.log('popularConversations', this.popularConversations);
        return Promise.resolve(this.popularConversations || []);
      })
      .catch(e => {
        this.log('error getting conversations', e);
        const toast = this.toastCtrl.create({
          message:
            'An error occurred while retrieving your conversations. Please check your internet connection and try again. If the issue continues, please contact us for help.',
          position: 'top',
          duration: 5000,
        });
        toast.present();
        return Promise.reject('error getting conversations');
      });
  }

  async getActiveRooms(): Promise<any> {
    //get all rooms user has been active in
    return this.crudApiProvider
      .get('/api/v2/lc/rooms/active', {
        sortBy: 'dates.updated:asc',
      })
      .then(async res => {
        this.activeConversations = res.data;
        return Promise.resolve(this.activeConversations || []);
      })
      .catch(e => {
        this.log('error getting conversations', e);
        const toast = this.toastCtrl.create({
          message:
            'An error occurred while retrieving your conversations. Please check your internet connection and try again. If the issue continues, please contact us for help.',
          position: 'top',
          duration: 5000,
        });
        toast.present();
        return Promise.reject('error getting conversations');
      });
  }

  async discoverRooms(): Promise<any> {
    //show chat rooms user has not been apart of
    return this.crudApiProvider
      .get('/api/v2/lc/rooms/discover', {
        sortBy: 'dates.updated:asc',
      })
      .then(async res => {
        this.discoverConversations = res.data;
        this.log('discoverConversations', this.discoverConversations);
        return Promise.resolve(this.discoverConversations || []);
      })
      .catch(e => {
        this.log('error getting conversations', e);
        const toast = this.toastCtrl.create({
          message:
            'An error occurred while retrieving your conversations. Please check your internet connection and try again. If the issue continues, please contact us for help.',
          position: 'top',
          duration: 5000,
        });
        toast.present();
        return Promise.reject('error getting conversations');
      });
  }
  // ===============================================
  // ===============================================

  // createNewConversation(friendIds: any[], name = '', source?): Promise<any> {
  //   return this.doesConvoExist(friendIds).then(convo => {
  //     if (convo) {
  //       return Promise.resolve(convo);
  //     } else {
  //       return this.crudApiProvider
  //         .post('/api/v2/lc/rooms', {
  //           userIds: friendIds,
  //           name,
  //           source,
  //         })
  //         .then(res => {
  //           this.log('new convo', res.data);
  //           this.tools.logAnalyticsEvent('trackCustom', 'lc-conversation-create', {
  //             user: _.get(this.user, 'info._id'),
  //           });
  //           return Promise.resolve(res.data);
  //         })
  //         .catch(e => {
  //           this.log('error creating new conversation', e);
  //           this.tools.logAnalyticsEvent(
  //             'trackCustom',
  //             'lc-conversation-create-error',
  //             {
  //               user: _.get(this.user, 'info._id'),
  //             }
  //           );
  //           const toast = this.toastCtrl.create({
  //             message:
  //               'An error occurred while creating a new conversation. Please check your internet connection and try again. If the issue continues, please contact us for help.',
  //             position: 'top',
  //             duration: 5000,
  //           });
  //           toast.present();
  //         });
  //     }
  //   });
  // }

  doesConvoExist(friend): Promise<any> {
    let userId = _.get(this.user, 'info._id');
    let idArray = _.cloneDeep(friend);
    idArray.push(userId);

    return this.getConversations().then(convos => {
      let existingConvo;
      let conversations = _.cloneDeep(convos);
      let matchingConvos = _.filter(conversations, c => {
        let users = _.cloneDeep(_.get(c, 'users') || []);

        users = _.map(users, u => String(u._id));

        return _.isEqual(_.sortBy(users), _.sortBy(idArray));
      });

      matchingConvos = _.sortBy(matchingConvos, 'dates.created').reverse();
      existingConvo = _.first(matchingConvos) || false;

      return Promise.resolve(existingConvo);
    });
  }

  getConversation(_id: string, forceRefresh = false): Promise<any> {
    this.log(
      `getConversation | _id: ${forceRefresh ? 'forcing refresh' : ''}`,
      _id
    );
    const localConvo = this.getLocalRoom(_id);
    this.log('getConversation | localRoom', localConvo, localConvo?.dndUserIds);
    if (localConvo && !forceRefresh) {
      this.log('getConversation | found localConversation', _id, localConvo);
      return Promise.resolve(localConvo);
    }

    this.log('getConversation | FETCH', _id);
    return this.crudApiProvider
      .get(`/api/v2/lc/rooms/${_id}`)
      .then(async res => {
        const lr = await this.updateLocalConversation(_id, res.data);
        this.refreshLocalConversationsOnlineUsers();
        return Promise.resolve(lr || res.data);
      })
      .catch(e => {
        this.log('getConversation | error getting existing convo: ', e);
        this.tools.logAnalyticsEvent(
          'trackCustom',
          'lc-conversation-get-error',
          {
            user: _.get(this.user, 'info._id'),
          }
        );
        return Promise.reject(e);
      });
  }

  sendSupportQuestion(): Promise<any> {
    const supportRoute = '/api/v2/lc/messages/community-support';

    return this.crudApiProvider
      .post(supportRoute)
      .then(response => {
        // this.tools.logEvent('directMessage');
        // this.tools.logAnalyticsEvent('trackCustom', 'lc-message-sent', {
        //   user: _.get(this.user, 'info._id'),
        // });

        return Promise.resolve(response.data);
      })
      .catch(e => {});
  }

  sendMessage(messageObj, socketId): Promise<any> {
    const route = _.get(messageObj, 'messageId')
      ? '/api/v2/lc/replies/' + messageObj.messageId
      : '/api/v2/lc/messages';

    this.log('sendMessage | route', route);
    this.log('sendMessage | message', messageObj);

    _.set(messageObj, 'dates', { created: Date.now() });

    if (_.get(messageObj, 'messageId')) {
      _.set(messageObj, 'sending', true);
      _.set(messageObj, 'user', this.user.getPublicInfo());
      this.addReplyToMessage(messageObj.conversationId, messageObj);
      this.emit('socket--message-create--' + socketId, messageObj);
    } else {
      _.set(messageObj, 'sending', true);
      _.set(messageObj, 'user', this.user.getPublicInfo());
      this.addMessageToRoom(messageObj.conversationId, messageObj);
      this.emit('socket--message-create--' + socketId, messageObj);
    }

    return this.crudApiProvider
      .post(route, messageObj)
      .then(response => {
        this.tools.logEvent('directMessage');
        this.tools.logAnalyticsEvent('trackCustom', 'lc-message-sent', {
          user: _.get(this.user, 'info._id'),
        });

        return Promise.resolve(response.data);
      })
      .catch(e => {
        this.log('error posting new message', e);
        if (e && e?.error?.error === 'MediaViolationDetection') {
          this.alertCtrl
            .create({
              title: e?.error?.title || 'Content Violation',
              message: e?.error?.message,
              buttons: [
                {
                  text: 'OK',
                  role: 'cancel',
                  handler: data => {
                    console.warn('OK clicked');
                  },
                },
              ],
            })
            .present();
        } else {
          this.alertCtrl
            .create({
              title: "Oh no, we're sorry!",
              message:
                'An error occurred while sending your message. Please check your connection and try again. If the issue continues, please contact us for help.',
              buttons: [
                {
                  text: 'OK',
                  role: 'cancel',
                  handler: data => {
                    console.warn('OK clicked');
                  },
                },
              ],
            })
            .present();
        }
        this.tools.logAnalyticsEvent('trackCustom', 'lc-message-send-error', {
          user: _.get(this.user, 'info._id'),
        });
        return Promise.reject(e);
      });
  }

  addReaction = _.debounce((mId: any, addRemove: string, emoji: string) => {
    this.log('localConvos', this.localConversations);
    this.crudApiProvider.post(`/api/v2/lc/reactions/${mId}/${addRemove}`, {
      type: emoji,
    });
    this.tools.logAnalyticsEvent('trackCustom', `lc-reaction-${addRemove}`, {
      user: _.get(this.user, 'info._id'),
    });
  }, 1000);

  // checkAutoJoin(socketId: string) {
  //   const room = _.find(
  //     this.localConversations,
  //     convo => convo.socketId === socketId
  //   );
  //   this.log('checkAutoJoin | roomId', socketId, room);

  //   if (room?.autoJoin) {
  //     this.joinRoomSocket(socketId);
  //   }
  // }

  async hasJoinedRoom(conversationId: string): Promise<boolean> {
    const convo = await this.getConversation(conversationId).catch(e => {
      return Promise.resolve(false);
    });

    if (!convo) return Promise.resolve(false);
    return _.includes(convo.userIds, this.user.info._id);
  }

  async hasIgnoredRoomContest(conversationId: string): Promise<boolean> {
    return !!localStorage.getItem('cdlc-ignored-contest--' + conversationId);
  }
}



// WEBPACK FOOTER //
// ./src/providers/live-chat/live-chat.ts